<template>
  <button
    :type="type"
    :disabled="disabled || isLoading"
    :class="[
      classes.base,
      classes.variant[variant],
      {
        [classes.disabledBase]: disabled,
        [classes.disabled[variant]]: disabled,
        [classes.hover[variant]]: !disabled && !isLoading,
      },
    ]"
    @click.prevent="emit('click')"
  >
    <LoadingSpinner
      v-if="isLoading"
      :color="classes.loadingColor[props.variant]"
      :size="loadingSize"
      :stroke="loadingStroke"
    />

    <div v-else class="flex items-center justify-center">
      <Icon v-if="iconOnLeftSide" class="mr-2" :name="iconOnLeftSide" :size="iconSize" />
      <slot />
      <Icon v-if="iconOnRightSide" class="ml-2" :name="iconOnRightSide" :size="iconSize" />
    </div>
  </button>
</template>

<script setup lang="ts">
import { IconName } from "../utils";

import Icon from "./Icon.vue";
import LoadingSpinner, { ColorsName } from "./LoadingSpinner.vue";

export type ButtonVariant =
  | "elevated"
  | "outlined"
  | "outlinedAccept"
  | "outlinedRefuse"
  | "outlinedError"
  | "text";

const props = withDefaults(
  defineProps<{
    type?: "submit" | "button" | "reset";
    variant?: ButtonVariant;
    disabled?: boolean;
    isLoading?: boolean;
    loadingSize?: number;
    loadingStroke?: number;
    loadingColor?: ColorsName;
    iconOnRightSide?: IconName;
    iconOnLeftSide?: IconName;
    iconSize?: string;
  }>(),
  {
    type: "submit",
    variant: "elevated",
    disabled: false,
    isLoading: false,
    loadingSize: 20,
    loadingStroke: 3,
    loadingColor: undefined,
    iconOnRightSide: undefined,
    iconOnLeftSide: undefined,
    iconSize: undefined,
  }
);

interface Classes {
  base: string;
  disabledBase: string;
  variant: { [Key in ButtonVariant]: string };
  hover: { [Key in ButtonVariant]: string };
  disabled: { [Key in ButtonVariant]: string };
  loadingColor: { [Key in ButtonVariant]: ColorsName };
}

const classes: Classes = {
  base: "flex items-center justify-center transition ease-in-out duration-200 min-h-button w-full max-w-button p-2",
  disabledBase: "opacity-50 cursor-default",
  variant: {
    elevated: "bg-primary text-black rounded",
    outlined: "border-2 border-tertiary text-secondary rounded",
    outlinedAccept: "border-2 border-tertiary text-success rounded",
    outlinedRefuse: "border-2 border-tertiary text-error rounded",
    outlinedError: "border-2 border-error text-error rounded",
    text: "text-secondary",
  },
  hover: {
    elevated: "hover:opacity-75",
    outlined: "hover:opacity-75",
    outlinedAccept: "hover:opacity-75",
    outlinedRefuse: "hover:opacity-75",
    outlinedError: "hover:opacity-75",
    text: "hover:opacity-75",
  },
  disabled: {
    elevated: "",
    outlined: "text-gray border-gray",
    outlinedAccept: "text-gray border-gray",
    outlinedRefuse: "text-gray border-gray",
    outlinedError: "text-gray border-gray",
    text: "",
  },
  loadingColor: {
    elevated: props.loadingColor ?? "body",
    outlined: props.loadingColor ?? "secondary",
    outlinedAccept: props.loadingColor ?? "success",
    outlinedRefuse: props.loadingColor ?? "error",
    outlinedError: props.loadingColor ?? "error",
    text: props.loadingColor ?? "secondary",
  },
};

const emit = defineEmits<{ (event: "click"): void }>();
</script>
